import {
  AnalyticsComponentProvider,
  AnalyticsScreenProvider,
  AppShell,
  Callout,
  Card,
  Flex,
  Link,
  PageGridCell,
  PageGridRow,
  StandardLayout,
  Tabs,
  Text,
} from "@wayflyer/flyui";
import { ChartFilterSwitches, DatePickerHeader, TemporalResolutionPickers } from "@wayflyer/flyui-dataviz";
import { useIntl } from "react-intl";

import { InsightsDataTable } from "../../components/insights-data-table";
import { OverviewPlaceholder } from "../../components/overview-placeholder/overview-placeholder";
import { PinnedMetricsSelector } from "../../components/pinned-metrics/pinned-metrics";
import { RoasWarning } from "../../components/roas-warning/roas-warning";
import { Spinner } from "../../components/styled";
import type { OverviewProps } from "../../interface";
import { AdditionalCostsForm } from "../additional-costs-form";
import { OverviewMetricCards } from "./metric-cards";
import { OverviewProfitChart } from "./profit-chart";

const fullWidth = { xxs: { span: 6 }, sm: { span: 8 }, md: { span: 12 } };

export const InternalOverview = (props: OverviewProps) => {
  const intl = useIntl();
  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...switchProps } = props.chartFilters;
  const error = props.chartDataError || props.summaryDataError;
  const pinnedTitle =
    props.tabTitlePinnedMetrics ?? intl.formatMessage({ id: "Key Metrics", defaultMessage: "Key Metrics" });
  const tableTitle =
    props.tabTitleTable ?? intl.formatMessage({ id: "Detailed Performance", defaultMessage: "Detailed Performance" });
  const chartTitle = intl.formatMessage({ id: "Overview: profitability chart", defaultMessage: "Profitability" });
  const keyMetrics = props.pinnedMetricsProps ? (
    <AnalyticsComponentProvider component="key-metrics">
      <>
        <PageGridRow>
          <PageGridCell layout={{ xxs: { span: 6 }, sm: { span: 8 }, md: { span: 4 } }}>
            <Flex direction="column" padding={["0", "0", "6", "0"]}>
              <PinnedMetricsSelector {...props.pinnedMetricsProps} wrapSelection />
            </Flex>
          </PageGridCell>
        </PageGridRow>
        <PageGridRow>
          <PageGridCell layout={fullWidth}>
            {props.pinnedMetricsProps.metrics.length ? (
              <OverviewMetricCards
                metrics={props.pinnedMetricsProps.metrics.filter((metric) => metric.enabled)}
                loading={props.pinnedMetricsProps.loading}
                label={pinnedTitle}
              />
            ) : (
              <Spinner />
            )}
          </PageGridCell>
        </PageGridRow>
      </>
    </AnalyticsComponentProvider>
  ) : null;
  const table = (
    <AnalyticsComponentProvider component="detailed-performance">
      <PageGridRow>
        <PageGridCell layout={fullWidth}>
          {props.tableDataError ? (
            <Callout variant="critical" description={props.tableDataError} />
          ) : props.tableDataLoading ? (
            <Spinner />
          ) : props.tableProps.columns?.length ? (
            <InsightsDataTable
              {...props.tableProps}
              title={props.subtitle}
              enableCSVDownload
              csvFilename="wayflyer-insights-overview-data.csv"
            />
          ) : null}
        </PageGridCell>
      </PageGridRow>
    </AnalyticsComponentProvider>
  );
  return (
    <AnalyticsScreenProvider screen="internal-overview">
      <AppShell>
        <StandardLayout title={props.showAmazonPlaceholder ? "Overview" : props.pageTitle}>
          {props.showAmazonPlaceholder ? (
            <PageGridRow>
              <PageGridCell layout={fullWidth}>
                <OverviewPlaceholder supportLink={props.supportLink} />
                <Link url={props.learnMoreLink} external>
                  {intl.formatMessage({
                    id: "overview placeholder: learn more",
                    defaultMessage: "Learn more about insights",
                  })}
                </Link>
              </PageGridCell>
            </PageGridRow>
          ) : (
            <>
              <PageGridRow>
                <PageGridCell layout={fullWidth}>
                  <AnalyticsComponentProvider component="global-filters">
                    <Flex direction="column" gap="4">
                      {props.hint ? <Text>{props.hint}</Text> : null}
                      {props.datePickerHeaderProps ? (
                        <DatePickerHeader {...props.datePickerHeaderProps} useSimplePanel inlineLeft />
                      ) : null}
                      <RoasWarning isAmazonCustomer={props.isAmazonCustomer} roasEnabled={props.roasEnabled} />
                    </Flex>
                  </AnalyticsComponentProvider>
                </PageGridCell>
              </PageGridRow>
              <PageGridRow>
                <PageGridCell layout={fullWidth}>
                  <AnalyticsComponentProvider component="summary-metrics">
                    <OverviewMetricCards
                      metrics={props.summaryData}
                      loading={props.summaryDataLoading}
                      onSelect={props.onSelectMetric}
                      referenceText={intl.formatMessage({
                        id: "Overview 'vs last year' suffix",
                        defaultMessage: "vs last year",
                      })}
                      label={intl.formatMessage({ id: "Overview: summary metrics", defaultMessage: "Summary metrics" })}
                    />
                  </AnalyticsComponentProvider>
                </PageGridCell>
              </PageGridRow>
              <PageGridRow>
                <PageGridCell layout={{ xxs: { span: 6 }, sm: { span: 8 }, md: { span: 9 } }}>
                  <AnalyticsComponentProvider component="profitability">
                    <section aria-label={chartTitle}>
                      <Card
                        slots={{
                          header: (
                            <Card.ChartHeader
                              title={chartTitle}
                              slots={{
                                controls: (
                                  <Flex wrap="wrap" gap="4" align="center">
                                    <TemporalResolutionPickers
                                      onClickDateRes={onClickDateRes}
                                      hideWeekly={hideWeekly}
                                      hideMonthly={hideMonthly}
                                      selectedButtonIndex={selectedButtonIndex}
                                    />
                                    <ChartFilterSwitches
                                      {...switchProps}
                                      switchOneOn={props.thisYearEnabled}
                                      switchTwoOn={props.lastYearEnabled}
                                    />
                                  </Flex>
                                ),
                              }}
                            />
                          ),
                        }}
                      >
                        {error ? <Callout variant="critical" description={error} /> : null}
                        <OverviewProfitChart
                          {...props.chartData}
                          data={props.chartData?.data ?? []}
                          formatValue={props.chartData?.formatValue ?? String}
                          loading={props.chartDataLoading}
                          error={error}
                          thisYearEnabled={props.thisYearEnabled}
                          lastYearEnabled={props.lastYearEnabled}
                          profitEnabled={props.profitEnabled}
                          salesEnabled={props.salesEnabled}
                          salesIncReturnsEnabled={props.salesIncReturnsEnabled}
                          marketingEnabled={props.marketingEnabled}
                          roasEnabled={props.roasEnabled}
                          aovEnabled={props.aovEnabled}
                          cacEnabled={props.cacEnabled}
                        />
                      </Card>
                    </section>
                  </AnalyticsComponentProvider>
                </PageGridCell>
                {!error ? (
                  <PageGridCell layout={{ xxs: { span: 6 }, sm: { span: 8 }, md: { span: 3 } }}>
                    <AnalyticsComponentProvider component="additional-costs">
                      <Card
                        slots={{
                          header: (
                            <Card.Header
                              title={intl.formatMessage({
                                id: "Additional Costs title",
                                defaultMessage: "Additional Costs",
                              })}
                              titleTooltip={intl.formatMessage({
                                id: "Additional costs tooltip",
                                defaultMessage:
                                  "Use this form to add any additional costs that arent captured from your shopping or ads platforms. Once entered a profitability metric will be added to the graph and table.",
                              })}
                            />
                          ),
                        }}
                      >
                        <AdditionalCostsForm
                          {...props.additionalCostsData}
                          additionalCostsDataError={props.additionalCostsDataError}
                          embedded
                        />
                      </Card>
                    </AnalyticsComponentProvider>
                  </PageGridCell>
                ) : null}
              </PageGridRow>
              <PageGridRow>
                <PageGridCell layout={fullWidth} subgrid>
                  {props.enableTabs && props.pinnedMetricsProps ? (
                    <Tabs
                      contentSubgrid
                      ariaLabel={props.pageTitle}
                      tabs={[
                        { title: pinnedTitle, content: keyMetrics },
                        { title: tableTitle, content: table },
                      ]}
                    />
                  ) : (
                    <>
                      {keyMetrics}
                      {table}
                    </>
                  )}
                </PageGridCell>
              </PageGridRow>
            </>
          )}
        </StandardLayout>
      </AppShell>
    </AnalyticsScreenProvider>
  );
};
